/**
 * Application settings → Run layouts: which layout the running application
 * opens in (`application.shell.defaultLayout`) and which ones its users may
 * pick from their user menu (`application.shell.allowedLayouts`). Classic is
 * always on offer — it is the phone-width layout and the one every user had.
 *
 * The layout list is data (`packages/page-runtime/src/shell/nav/shellConfig.json`);
 * the backend normalises the block on save (nav_edit / shell_config).
 */
import { LAYOUTS, LayoutThumb, type AppShellSettings, type LayoutId } from '@twasta/page-runtime/shell'

interface Props {
  value?: AppShellSettings | null
  onChange: (next: AppShellSettings) => void
  disabled?: boolean
}

export default function ShellLayoutSettings({ value, onChange, disabled = false }: Props) {
  const shell = value || {}
  const allowedRaw = (shell.allowedLayouts || []).filter((id) => LAYOUTS.some((l) => l.id === id))
  // Empty list ⇒ every layout is offered.
  const allowed: LayoutId[] = allowedRaw.length ? (allowedRaw as LayoutId[]) : LAYOUTS.map((l) => l.id)
  const def = (LAYOUTS.find((l) => l.id === shell.defaultLayout)?.id || 'classic') as LayoutId

  const setAllowed = (id: LayoutId, on: boolean) => {
    if (id === 'classic') return
    const next = on ? [...new Set([...allowed, id])] : allowed.filter((x) => x !== id)
    const all = LAYOUTS.every((l) => l.id === 'classic' || next.includes(l.id))
    onChange({
      ...shell,
      // Every layout ticked is stored as "no restriction".
      allowedLayouts: all ? [] : LAYOUTS.map((l) => l.id).filter((x) => x === 'classic' || next.includes(x)),
      defaultLayout: next.includes(def) || def === 'classic' ? def : 'classic',
    })
  }

  return (
    <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1rem', display: 'grid', gap: '0.75rem' }}>
      <div>
        <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, margin: 0 }}>Run layouts</h3>
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>
          How the running application arranges its menus. Each user can switch layout from their
          user menu; choose the one it opens in and which ones they may pick.
        </p>
      </div>
      <label style={{ display: 'grid', gap: 4, fontSize: '0.875rem', fontWeight: 500, maxWidth: 320 }}>
        Default layout
        <select
          value={def}
          disabled={disabled}
          data-tw-field="shell_default_layout"
          onChange={(e) => onChange({ ...shell, defaultLayout: e.target.value })}
        >
          {LAYOUTS.filter((l) => allowed.includes(l.id)).map((l) => (
            <option key={l.id} value={l.id}>{l.label}</option>
          ))}
        </select>
      </label>
      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: 6 }}>Layouts users may choose</legend>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8 }}>
          {LAYOUTS.map((l) => {
            const on = allowed.includes(l.id)
            return (
              <label key={l.id} title={l.description} style={{
                display: 'flex', flexDirection: 'column', gap: 6, padding: 8, borderRadius: 8, cursor: disabled ? 'default' : 'pointer',
                border: `1px solid ${on ? 'var(--color-accent)' : 'var(--color-border)'}`,
                background: on ? 'var(--color-accent-weak)' : 'transparent',
              }}>
                <LayoutThumb id={l.id} size={120} />
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5 }}>
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={disabled || l.id === 'classic'}
                    data-tw-field={`shell_allowed:${l.id}`}
                    onChange={(e) => setAllowed(l.id, e.target.checked)}
                  />
                  {l.label}{l.id === 'classic' ? ' (always)' : ''}
                </span>
              </label>
            )
          })}
        </div>
      </fieldset>
    </div>
  )
}
